import { CommonModule } from '@angular/common';
import { Component, inject, input, signal, ViewContainerRef } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDropdownModule } from 'ng-zorro-antd/dropdown';
import { NzFlexModule } from 'ng-zorro-antd/flex';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzTableModule, NzTableQueryParams } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { debounceTime, distinctUntilChanged, skip } from 'rxjs/operators';
import {
  IcdCodeHeaderRowItemSearchResult,
  IcdCodeType,
  VerifyIcdCodeWebRequest,
} from '../../../../core/api/mhsConfigurationApiClient';
import { booleanToString } from '../../../../core/utils/transformers';
import { CodesService } from '../../services/codes-service';
import { IcdManageMeta } from '../icd-manage-meta/icd-manage-meta';
import type { Filters } from './icd-code-filters';

@Component({
  selector: 'app-icd-code-list',
  imports: [
    CommonModule,
    FormsModule,
    NzTableModule,
    NzInputModule,
    NzButtonModule,
    NzIconModule,
    NzFlexModule,
    NzTagModule,
    NzDropdownModule,
    NzFlexModule,
    NzModalModule,
  ],
  templateUrl: './icd-code-list.html',
  styleUrl: './icd-code-list.scss',
})
export class IcdCodeListComponent {
  readonly viewContainerRef = inject(ViewContainerRef);
  readonly codeType = input.required<IcdCodeType>();
  readonly title = input.required<string>();
  readonly codesService = inject(CodesService);
  readonly router = inject(Router);
  readonly modalService = inject(NzModalService);
  readonly searchResult = signal<IcdCodeHeaderRowItemSearchResult | null>(null);
  readonly filters = signal<Filters>({
    query: '',
    isVerified: null,
  });
  readonly loading = signal(true);
  total = 0;
  pageSize = 10;
  pageIndex = 1;

  constructor() {
    toObservable(this.filters)
      .pipe(skip(1), debounceTime(500), distinctUntilChanged())
      .subscribe(() => this.reload());
  }

  queryChange(query: string): void {
    this.filters.update((f) => ({ ...f, query }));
  }

  reload(): void {
    this.pageIndex = 1;
    this.searchResult.set(null);
    this.load(this.pageIndex, this.pageSize);
  }

  onQueryParamsChange(params: NzTableQueryParams): void {
    const currentSort = params.sort.find((item) => item.value !== null);
    this.pageIndex = params.pageIndex;
    this.pageSize = params.pageSize;
    this.load(params.pageIndex, params.pageSize, currentSort?.key, currentSort?.value === 'ascend');
  }

  createClick(): void {
    this.router.navigate(['/codes', this.codeType().toLowerCase(), 'create']);
  }
  detailsClick(code: string): void {
    this.router.navigate(['/codes', this.codeType().toLowerCase(), code]);
  }
  editClick(code: string): void {
    this.router.navigate(['/codes', this.codeType().toLowerCase(), code, 'edit']);
  }
  isIcd10CodeType(): boolean {
    return this.codeType() === IcdCodeType.Icd10;
  }
  isUserActionsDisabled(data: { isVerified?: boolean | null }): boolean {
    //return !this.isIcd10CodeType() && !!data.isVerified;
    return !!data.isVerified;
  }
  showManageMetaClick(codeId: number): void {
    this.createManageMetadModal(codeId);
  }
  createManageMetadModal(codeId: number): void {
    const modal = this.modalService.create({
      nzTitle: 'Zarządzaj metadanymi kodu ICD',
      nzContent: IcdManageMeta,
      nzViewContainerRef: this.viewContainerRef,
      nzData: { codeId: codeId, codeType: this.codeType() },
      nzFooter: null,
      nzWidth: 600,
    });
  }
  verifyClick(codeId: number): void {
    this.modalService.confirm({
      nzTitle: 'Czy na pewno chcesz zweryfikować kod ICD?',
      nzContent: 'Kod zostanie zweryfikowany i nie będzie można go edytować.',
      nzOkText: 'Tak, zweryfikuj',
      nzCancelText: 'Anuluj',
      nzOkDanger: false,
      nzOnOk: () => {
        this.verify(codeId);
      },
    });
  }
  verify(codeId: number): void {
    const request = new VerifyIcdCodeWebRequest({
      type: this.codeType(),
      id: codeId,
      isVerified: true,
    });
    this.codesService.verify(request).subscribe({
      next: () => {
        this.modalService.success({
          nzTitle: 'Kod ICD został zweryfikowany',
        });
        this.reload();
      },
      error: (error) => {
        console.error('Error verifying ICD code:', error);
        this.modalService.error({
          nzTitle: 'Wystąpił błąd podczas weryfikacji kodu',
        });
      },
    });
  }
  private load(
    pageIndex: number,
    pageSize: number,
    orderBy?: string,
    isOrderByAsc?: boolean,
  ): void {
    this.loading.set(true);
    this.codesService
      .search(
        pageIndex,
        pageSize,
        this.codeType(),
        this.filters().query,
        booleanToString(this.filters().isVerified),
        orderBy,
        isOrderByAsc,
      )
      .subscribe({
        next: (result) => {
          this.searchResult.set(result);
          this.total = result.total ?? 0;
          this.loading.set(false);
        },
        error: (error) => {
          console.error('Error during ICD code search:', error);
          this.loading.set(false);
        },
      });
  }
}
